/**
 * 🏆 MILESTONE SYSTEM - Reward players for reaching goals
 *
 * Provides short-term (minutes), medium-term (30-90min), and long-term (hours) goals.
 * Players should feel "I'm always close to something big" via visible milestones.
 *
 * Each milestone awards resources when the threshold is crossed.
 * Milestones are one-time rewards (tracked in completedMilestones state).
 */

export const MILESTONES = {
  // ===========================================
  // 🏗️ BUILDING MILESTONES - Total buildings owned
  // ===========================================
  buildings: {
    10: {
      id: 'buildings_10',
      name: 'Growing Settlement',
      icon: '🏘️',
      rewards: { gold: 100 },
      description: 'Your settlement begins to take shape.',
    },
    25: {
      id: 'buildings_25',
      name: 'Thriving Village',
      icon: '🏡',
      rewards: { gold: 300, knowledge: 50 },
      description: 'A thriving village emerges from humble beginnings.',
    },
    50: {
      id: 'buildings_50',
      name: 'Prosperous Town',
      icon: '🏛️',
      rewards: { gold: 1000, knowledge: 200 },
      description: 'Your town prospers and attracts new settlers.',
    },
    100: {
      id: 'buildings_100',
      name: 'Great City',
      icon: '🏰',
      rewards: { gold: 5000, knowledge: 500, culture: 100 },
      description: 'A great city rises, the pride of your kingdom.',
    },
    200: {
      id: 'buildings_200',
      name: 'Mighty Empire',
      icon: '👑',
      rewards: { gold: 20000, magic: 500 },
      description: 'Your empire spans across the land.',
    },
    350: {
      id: 'buildings_350',
      name: 'World Wonder',
      icon: '🌟',
      rewards: { gold: 50000, magic: 1500, artifacts: 100 },
      description: 'Your civilization will be remembered for ages.',
    },
  },

  // ===========================================
  // 🔬 TECHNOLOGY MILESTONES - Total techs researched
  // ===========================================
  techs: {
    10: {
      id: 'techs_10',
      name: 'Scholar',
      icon: '📖',
      rewards: { knowledge: 150 },
      description: 'You begin to understand the ways of the world.',
    },
    25: {
      id: 'techs_25',
      name: 'Sage',
      icon: '📚',
      rewards: { knowledge: 500, science: 50 },
      description: 'Your wisdom grows with each discovery.',
    },
    50: {
      id: 'techs_50',
      name: 'Polymath',
      icon: '🎓',
      rewards: { knowledge: 2000, science: 200 },
      description: 'You master knowledge across many fields.',
    },
    70: {
      id: 'techs_70',
      name: 'Master of Knowledge',
      icon: '🔮',
      rewards: { knowledge: 10000, magic: 1000 },
      description: 'The secrets of the universe reveal themselves to you.',
    },
  },

  // ===========================================
  // 🗺️ TILE MILESTONES - Tiles explored on world map
  // ===========================================
  tiles: {
    25: {
      id: 'tiles_25',
      name: 'Frontier Explorer',
      icon: '🧭',
      rewards: { gold: 150, food: 150 },
      description: 'You venture beyond the known lands.',
    },
    50: {
      id: 'tiles_50',
      name: 'Pathfinder',
      icon: '🗺️',
      rewards: { gold: 500, knowledge: 100 },
      description: 'Your maps expand to new territories.',
    },
    100: {
      id: 'tiles_100',
      name: 'Realm Mapper',
      icon: '🌍',
      rewards: { gold: 2000, knowledge: 500 },
      description: 'The realm unfolds before your explorers.',
    },
    200: {
      id: 'tiles_200',
      name: 'World Discoverer',
      icon: '🌐',
      rewards: { gold: 10000, magic: 300 },
      description: 'No corner of the world remains hidden.',
    },
  },

  // ===========================================
  // 📜 QUEST MILESTONES - Total quests completed
  // (Aligned with chapter endings: 12 quests per chapter)
  // ===========================================
  quests: {
    12: {
      id: 'quests_12',
      name: 'Chapter 1 Complete',
      icon: '📜',
      rewards: { gold: 200 },
      description: 'The Awakening chapter is complete.',
    },
    24: {
      id: 'quests_24',
      name: 'Chapter 2 Complete',
      icon: '📜',
      rewards: { gold: 500, knowledge: 100 },
      description: 'The Settlement chapter is complete.',
    },
    36: {
      id: 'quests_36',
      name: 'Chapter 3 Complete',
      icon: '📜',
      rewards: { gold: 1000, knowledge: 250 },
      description: 'The Iron Age chapter is complete.',
    },
    48: {
      id: 'quests_48',
      name: 'Chapter 4 Complete',
      icon: '📜',
      rewards: { gold: 2000, knowledge: 400, culture: 50 },
      description: 'The Commerce chapter is complete.',
    },
    60: {
      id: 'quests_60',
      name: 'Chapter 5 Complete',
      icon: '📜',
      rewards: { gold: 4000, knowledge: 600, science: 100 },
      description: 'The Industry chapter is complete.',
    },
    72: {
      id: 'quests_72',
      name: 'Chapter 6 Complete',
      icon: '📜',
      rewards: { gold: 8000, knowledge: 800, energy: 200 },
      description: 'The Power chapter is complete.',
    },
    84: {
      id: 'quests_84',
      name: 'Chapter 7 Complete',
      icon: '📜',
      rewards: { gold: 15000, knowledge: 1000, culture: 300 },
      description: 'The Empire chapter is complete.',
    },
    96: {
      id: 'quests_96',
      name: 'Chapter 8 Complete',
      icon: '📜',
      rewards: { gold: 25000, science: 500, magic: 200 },
      description: 'The Science chapter is complete.',
    },
    108: {
      id: 'quests_108',
      name: 'Chapter 9 Complete',
      icon: '📜',
      rewards: { gold: 40000, magic: 1000, artifacts: 100 },
      description: 'The Magic chapter is complete.',
    },
    120: {
      id: 'quests_120',
      name: 'Questmaster',
      icon: '👑',
      rewards: { gold: 50000, magic: 5000, artifacts: 500, crystals: 100 },
      description: 'All quests complete! A true master of the realm.',
    },
  },

  // ===========================================
  // ⚔️ DUNGEON MILESTONES - Dungeons completed
  // ===========================================
  dungeons: {
    1: {
      id: 'dungeons_1',
      name: 'First Delve',
      icon: '🏚️',
      rewards: { gold: 100 },
      description: 'You survived your first dungeon!',
    },
    5: {
      id: 'dungeons_5',
      name: 'Dungeon Crawler',
      icon: '⚔️',
      rewards: { gold: 500, knowledge: 100 },
      description: 'The darkness holds no fear for you.',
    },
    15: {
      id: 'dungeons_15',
      name: 'Veteran Adventurer',
      icon: '🗡️',
      rewards: { gold: 2000, magic: 200 },
      description: 'Countless dungeons have fallen to your blade.',
    },
    30: {
      id: 'dungeons_30',
      name: 'Legendary Hero',
      icon: '🏆',
      rewards: { gold: 10000, magic: 1000, artifacts: 50 },
      description: 'Your legend echoes through the depths.',
    },
  },

  // ===========================================
  // 🏛️ WONDER MILESTONES - Wonders built
  // ===========================================
  wonders: {
    1: {
      id: 'wonders_1',
      name: 'First Wonder',
      icon: '🏛️',
      rewards: { gold: 500, culture: 50 },
      description: 'Your first wonder stands as a testament to progress.',
    },
    3: {
      id: 'wonders_3',
      name: 'Wonder Builder',
      icon: '🏗️',
      rewards: { gold: 2000, culture: 200, knowledge: 300 },
      description: 'Great monuments mark your kingdom.',
    },
    5: {
      id: 'wonders_5',
      name: 'Wonder Master',
      icon: '🌟',
      rewards: { gold: 10000, magic: 500, artifacts: 100 },
      description: 'Your wonders inspire awe across the land.',
    },
  },
};

/**
 * Get all milestones for a specific category
 * @param {string} category - 'buildings', 'techs', 'tiles', 'quests', 'dungeons', 'wonders'
 * @returns {Object} Milestones for that category
 */
export function getMilestones(category) {
  return MILESTONES[category] || {};
}

/**
 * Check if a milestone threshold is reached
 * @param {string} category - Milestone category
 * @param {number} currentValue - Current count (buildings owned, techs researched, etc.)
 * @param {Set|Array} completedMilestones - Already completed milestone IDs
 * @returns {Object|null} The milestone to award, or null if none
 */
export function checkMilestone(category, currentValue, completedMilestones) {
  const milestones = MILESTONES[category];
  if (!milestones) return null;

  const completed = completedMilestones instanceof Set
    ? completedMilestones
    : new Set(completedMilestones || []);

  // Check thresholds in order (sorted ascending)
  const thresholds = Object.keys(milestones).map(Number).sort((a, b) => a - b);

  for (const threshold of thresholds) {
    const milestone = milestones[threshold];
    if (currentValue >= threshold && !completed.has(milestone.id)) {
      return milestone;
    }
  }

  return null;
}

/**
 * Get the next milestone for a category
 * @param {string} category - Milestone category
 * @param {number} currentValue - Current count
 * @param {Set|Array} completedMilestones - Already completed milestone IDs
 * @returns {Object|null} Next milestone to work toward, or null if all complete
 */
export function getNextMilestone(category, currentValue, completedMilestones) {
  const milestones = MILESTONES[category];
  if (!milestones) return null;

  const completed = completedMilestones instanceof Set
    ? completedMilestones
    : new Set(completedMilestones || []);

  const thresholds = Object.keys(milestones).map(Number).sort((a, b) => a - b);

  for (const threshold of thresholds) {
    const milestone = milestones[threshold];
    if (!completed.has(milestone.id)) {
      return {
        ...milestone,
        threshold,
        progress: currentValue,
        remaining: Math.max(0, threshold - currentValue),
      };
    }
  }

  return null; // All milestones complete
}

/**
 * Get milestone progress summary for UI display
 * @param {Object} counts - { buildings, techs, tiles, quests, dungeons, wonders }
 * @param {Set|Array} completedMilestones - Already completed milestone IDs
 * @returns {Array} Array of next milestones with progress info
 */
export function getMilestoneProgress(counts, completedMilestones) {
  const categories = ['buildings', 'techs', 'tiles', 'quests', 'dungeons', 'wonders'];
  const progress = [];

  for (const category of categories) {
    const currentValue = counts[category] || 0;
    const next = getNextMilestone(category, currentValue, completedMilestones);
    if (next) {
      progress.push({
        category,
        ...next,
        percentComplete: Math.min(100, Math.round((next.progress / next.threshold) * 100)),
      });
    }
  }

  // Sort by percent complete (closest to completion first)
  return progress.sort((a, b) => b.percentComplete - a.percentComplete);
}

export default MILESTONES;
